import {useEffect, useState} from "react"
import styles from "./DocumentTemplate.module.css"
import {Checkbox, Col, Row} from "antd"
import {FormLabel} from "components/Form"
import {BaseButton, BaseInput} from "components"
import DepartmentSubunitSelect from "components/DepartmentSubunitSelect"
import {useAllDocumentTemplates, useModel, useVisible} from "hooks"
import {KlassDropdown} from "components/Select"
import {DocumentTemplate as DocumentTemplateType} from "types/settings/document-templates"

export type DocumentTemplateItem = {
    documentName?: string
    description?: string
    departmentSubunit?: any
    isVisibleForUser?: boolean
    notifyStudent?: boolean
    presetTemplate?: DocumentTemplateType
    isOriginal?: boolean
}

type DocumentTemplateProps = {
    accessibleSubunitIds: number[]
    onApply: (template: DocumentTemplateItem) => void
}

const INITIAL_DOCUMENT_INFO: DocumentTemplateItem = {
    documentName: "",
    description: "",
    departmentSubunit: null
}

export function DocumentTemplate(props: DocumentTemplateProps) {
    const {onApply, accessibleSubunitIds} = props
    const [template, setTemplate] = useState<DocumentTemplateItem>(INITIAL_DOCUMENT_INFO)
    const model = useModel()
    const isStaffOrAdmin = model.isStaffOrAdmin()
    const {templates: presetTemplates} = useAllDocumentTemplates({enabled: isStaffOrAdmin})
    const toggleTemplate = useVisible(false)

    useEffect(() => {
        setTemplate({...INITIAL_DOCUMENT_INFO, isVisibleForUser: !isStaffOrAdmin, isOriginal: !isStaffOrAdmin})
    }, [isStaffOrAdmin])

    const handleFieldChange = (key: keyof DocumentTemplateItem, value: any) => {
        const _template = {...template, [key]: value}
        setTemplate(_template)
    }

    const onApplyTemplate = () => {
        onApply(template)
    }

    return (
        <div className={styles.container}>
            <div
                className={styles.toggleWrap}
                onClick={() => {
                    console.log("@toggle", toggleTemplate.isVisible)
                    toggleTemplate.toggle()
                }}>
                <Checkbox checked={toggleTemplate.isVisible} />
                <span className={styles.title}>APPLY TO ALL DOCUMENTS BELOW</span>
            </div>
            {toggleTemplate.isVisible && (
                <div className={styles.contentContainer}>
                    <Row gutter={[12, 12]}>
                        <Col span={isStaffOrAdmin ? 6 : 8}>
                            <FormLabel label="Document Name" />
                            <KlassDropdown
                                creatable
                                placeholder="Select or enter name"
                                formatCreateLabel={(inputValue) => `Use "${inputValue}"`}
                                isValidNewOption={(inputValue: string) => !!inputValue}
                                getNewOptionData={(inputValue: string) => ({
                                    id: 0,
                                    name: inputValue
                                })}
                                options={presetTemplates}
                                valueKey="id"
                                labelKey="name"
                                value={
                                    template.presetTemplate
                                        ? presetTemplates.find((x) => x.id === template.presetTemplate.id) ?? {
                                              id: 0,
                                              name: template.presetTemplate.name
                                          }
                                        : undefined
                                }
                                onChange={(option: DocumentTemplateType) => {
                                    let _template: DocumentTemplateItem = {
                                        ...template,
                                        presetTemplate: option,
                                        documentName: option?.name
                                    }
                                    if (option && option.id !== 0) {
                                        _template.description = option.description
                                        _template.departmentSubunit = option.departmentSubunit
                                    }
                                    setTemplate(_template)
                                }}
                            />
                        </Col>
                        <Col span={isStaffOrAdmin ? 6 : 10}>
                            <FormLabel label="Description" />
                            <BaseInput
                                placeholder="description"
                                value={template.description}
                                onChange={(v) => handleFieldChange("description", v)}
                            />
                        </Col>
                        <Col span={6}>
                            <FormLabel label="Department" />
                            <DepartmentSubunitSelect
                                subunitIds={accessibleSubunitIds}
                                placeholder="department"
                                value={template.departmentSubunit}
                                onChange={(v) => handleFieldChange("departmentSubunit", v)}
                            />
                        </Col>
                        {isStaffOrAdmin && (
                            <>
                                <Col span={2}>
                                    <FormLabel label="Visible to user" className={styles.visible} />
                                    <div className={styles.checkbox}>
                                        <Checkbox
                                            checked={template.isVisibleForUser}
                                            onChange={(v) => handleFieldChange("isVisibleForUser", v.target.checked)}
                                        />
                                    </div>
                                </Col>
                                <Col span={2}>
                                    <FormLabel label="Notify student" className={styles.visible} />
                                    <div className={styles.checkbox}>
                                        <Checkbox
                                            checked={template.notifyStudent}
                                            onChange={(v) => handleFieldChange("notifyStudent", v.target.checked)}
                                        />
                                    </div>
                                </Col>
                                <Col span={2}>
                                    <FormLabel label="Original" className={styles.visible} />
                                    <div className={styles.checkbox}>
                                        <Checkbox
                                            checked={template.isOriginal}
                                            onChange={(v) => handleFieldChange("isOriginal", v.target.checked)}
                                        />
                                    </div>
                                </Col>
                            </>
                        )}
                    </Row>
                    <div className={styles.action}>
                        <BaseButton title="Apply for all" onClick={onApplyTemplate} />
                    </div>
                </div>
            )}
        </div>
    )
}
